import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Types for our search results
export interface SearchResult {
  id: string;
  type: string;
  data: any;
  timestamp: number;
  loading?: boolean;
}

export interface AssistantMessage {
  id: string;
  content: string;
  timestamp: number;
  // Reasoning message properties
  isReasoning?: boolean;
  reasoningText?: string;
  reasoningDuration?: number;
}

export interface SearchState {
  results: SearchResult[];
  messages: AssistantMessage[];
  searchParams: {
    query: string;
    model: string;
    agent: string;
    agentParams?: any;
  };
  timestamp: number;
}

interface SearchResultsState {
  currentState: SearchState;
  history: SearchState[];
  currentIndex: number;
}

type SearchResultsAction =
  | { type: 'SET_RESULTS'; payload: SearchResult[] }
  | { type: 'APPEND_RESULTS'; payload: SearchResult[] }
  | { type: 'PREPEND_RESULTS'; payload: SearchResult[] }
  | { type: 'INSERT_RESULTS'; payload: { results: SearchResult[]; index: number } }
  | { type: 'REMOVE_RESULTS'; payload: { startIndex: number; count: number } }
  | { type: 'ADD_MESSAGE'; payload: AssistantMessage }
  | { type: 'UPDATE_MESSAGE'; payload: { id: string; content: string } }
  | { type: 'REMOVE_MESSAGE'; payload: { id: string } }
  | { type: 'SET_SEARCH_PARAMS'; payload: SearchState['searchParams'] }
  | { type: 'CLEAR_CURRENT_STATE' }
  | { type: 'GO_BACK' }
  | { type: 'GO_FORWARD' }
  | { type: 'UPDATE_BLOCK'; payload: { id: string; content: any; type: string; loading?: boolean } }
  | { type: 'RESET' };

const initialState: SearchResultsState = {
  currentState: {
    results: [],
    messages: [],
    searchParams: {
      query: '',
      model: 'gpt-4.1-mini',
      agent: 'search_engine',
    },
    timestamp: Date.now(),
  },
  history: [],
  currentIndex: -1,
};

function searchResultsReducer(state: SearchResultsState, action: SearchResultsAction): SearchResultsState {
  // Debug logging for state updates
  console.log("SearchResults Action:", {
    type: action.type,
    timestamp: new Date().toISOString(),
    currentIndex: state.currentIndex,
    historyLength: state.history.length
  });

  // Helper function to save current state to history
  const saveToHistory = (newState: SearchState) => {
    console.log("Saving to history:", {
      timestamp: new Date().toISOString(),
      currentIndex: state.currentIndex,
      newIndex: state.currentIndex + 1
    });
    const newHistory = [...state.history.slice(0, state.currentIndex + 1), newState];
    return {
      ...state,
      currentState: newState,
      history: newHistory,
      currentIndex: newHistory.length - 1,
    };
  };

  // Helper function to update current state by updating history entry
  const updateCurrentState = (newState: Partial<SearchState>) => {
    console.log("Updating current state:", {
      timestamp: new Date().toISOString(),
      currentIndex: state.currentIndex,
      historyLength: state.history.length
    });
    
    // Update the history entry at currentIndex
    const updatedCurrentState = {
      ...state.currentState,
      ...newState,
      timestamp: Date.now(),
    };
    
    const newHistory = [...state.history];
    newHistory[state.currentIndex] = updatedCurrentState;
    
    return {
      ...state,
      currentState: updatedCurrentState,
      history: newHistory,
    };
  };

  // Helper function to update both current state and history
  const updateCurrentAndHistory = (newState: Partial<SearchState>) => {
    const updatedCurrentState = {
      ...state.currentState,
      ...newState,
      timestamp: Date.now(),
    };
    
    // Update the history entry at currentIndex
    const newHistory = [...state.history];
    newHistory[state.currentIndex] = updatedCurrentState;
    
    return {
      ...state,
      currentState: updatedCurrentState,
      history: newHistory,
    };
  };

  switch (action.type) {
    case 'SET_RESULTS':
      // If current state has no results, just update it
      // If current state has results, create new state and move current to history
      if (state.currentState.results.length === 0) {
        return updateCurrentState({
          results: action.payload,
        });
      } else {
        return saveToHistory({
          ...state.currentState,
          results: action.payload,
          timestamp: Date.now(),
        });
      }

    case 'APPEND_RESULTS':
      // Don't save to history - just update current state
      return updateCurrentState({
        results: [...state.currentState.results, ...action.payload],
      });

    case 'PREPEND_RESULTS':
      // Don't save to history - just update current state
      return updateCurrentState({
        results: [...action.payload, ...state.currentState.results],
      });

    case 'INSERT_RESULTS':
      // Don't save to history - just update current state
      return updateCurrentState({
        results: [
          ...state.currentState.results.slice(0, action.payload.index),
          ...action.payload.results,
          ...state.currentState.results.slice(action.payload.index),
        ],
      });

    case 'REMOVE_RESULTS':
      // Don't save to history - just update current state
      return updateCurrentState({
        results: [
          ...state.currentState.results.slice(0, action.payload.startIndex),
          ...state.currentState.results.slice(action.payload.startIndex + action.payload.count),
        ],
      });

    case 'ADD_MESSAGE':
      // Don't save to history - just update current state
      return updateCurrentState({
        messages: [...state.currentState.messages, action.payload],
      });

    case 'UPDATE_BLOCK':
      // Don't save to history - just update current state
      return updateCurrentState({
        results: state.currentState.results.map(result => 
          result.id === action.payload.id 
            ? { ...result, data: action.payload.content, type: action.payload.type, loading: action.payload?.loading || false }
            : result
        ),
      });

    case 'UPDATE_MESSAGE':
      // Don't save to history - just update current state
      return updateCurrentState({
        messages: state.currentState.messages.map(msg => 
          msg.id === action.payload.id 
            ? { ...msg, content: msg.content + action.payload.content }
            : msg
        ),
      });

    case 'REMOVE_MESSAGE':
      // Don't save to history - just update current state
      return updateCurrentState({
        messages: state.currentState.messages.filter(msg => msg.id !== action.payload.id),
      });

    case 'SET_SEARCH_PARAMS':
      // Create new empty state and save current to history
      
      return saveToHistory({
        results: [],
        messages: [],
        searchParams: action.payload,
        timestamp: Date.now(),
      });

    case 'CLEAR_CURRENT_STATE':
      return saveToHistory({
        ...state.currentState,
        results: [],
        messages: [],
        timestamp: Date.now(),
      });

    case 'GO_BACK':
      if (state.currentIndex > 0) {
        return {
          ...state,
          currentState: state.history[state.currentIndex - 1],
          currentIndex: state.currentIndex - 1,
        };
      }
      return state;

    case 'GO_FORWARD':
      if (state.currentIndex < state.history.length - 1) {
        return {
          ...state,
          currentState: state.history[state.currentIndex + 1],
          currentIndex: state.currentIndex + 1,
        };
      }
      return state;

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

const SearchResultsContext = createContext<{
  state: SearchResultsState;
  dispatch: React.Dispatch<SearchResultsAction>;
} | undefined>(undefined);

export function SearchResultsProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(searchResultsReducer, initialState);

  return (
    <SearchResultsContext.Provider value={{ state, dispatch }}>
      {children}
    </SearchResultsContext.Provider>
  );
}

export function useSearchResults() {
  const context = useContext(SearchResultsContext);
  if (context === undefined) {
    throw new Error('useSearchResults must be used within a SearchResultsProvider');
  }
  return context;
} 